import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CrwdFloatingNotice } from '@crewdle/ui-kit';

import { NotificationService as DesktopNotificationService } from 'src/app/chat/core/services/notification.service';
import { PlatformService } from 'src/app/services/platform/platform.service';

const DISMISSED_KEY = 'notificationsNudgeDismissedAt';
/** How long "Not now" keeps the card away. */
export const NUDGE_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Show the nudge only where it can help: the browser supports notifications,
 * the site permission is still undecided (granted → nothing to ask; denied →
 * only the browser's site settings can undo it), not inside the native shell
 * (it owns its own prompt timing), and the user has not snoozed it.
 */
export function shouldShowNotificationsNudge(input: {
  supported: boolean;
  permission: NotificationPermission | null;
  inShell: boolean;
  dismissedAt: number | null;
  now: number;
}): boolean {
  if (!input.supported || input.inShell) return false;
  if (input.permission !== 'default') return false;
  if (input.dismissedAt && input.now - input.dismissedAt < NUDGE_SNOOZE_MS) return false;
  return true;
}

function readDismissedAt(): number | null {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    const at = raw ? Number(raw) : NaN;
    return Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
}

function writeDismissedAt(at: number): void {
  try {
    localStorage.setItem(DISMISSED_KEY, String(at));
  } catch { /* private mode — the card simply returns next load */ }
}

/**
 * "Get notified when your agents answer" — the one-click way to grant the
 * browser permission. The boot-time request is gesture-less, and Chrome answers
 * it with a quiet bell nobody notices (prod 2026-09-23: the sound played, no
 * notification ever showed). The click here is a real gesture, so the real
 * prompt appears; on grant a test notification proves the OS lets it through
 * (macOS can still block Chrome), and the Account-settings toggle flips on.
 */
@Component({
  selector: 'app-notifications-nudge',
  standalone: true,
  imports: [TranslateModule, CrwdFloatingNotice],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (visible()) {
      <!-- The kit's floating notice (bottom-end, safe-area aware, stacks its actions at 640px and below). -->
      <crwd-floating-notice
        placement="bottom-end"
        data-testid="notifications-nudge"
        [title]="'notifications-nudge.title' | translate"
        [detail]="(state() === 'denied' ? 'account.notifications-blocked' : 'notifications-nudge.detail') | translate"
        [actionLabel]="state() === 'idle' ? ('notifications-nudge.enable' | translate) : ''"
        [actionDisabled]="busy()"
        [dismissLabel]="(state() === 'denied' ? 'common.buttons.close' : 'notifications-nudge.later') | translate"
        (pressed)="enable()"
        (dismissed)="dismiss()"
      />
    }
  `,
  // Connect's accent on the action, as before the move (the white-label accent is not applied: deviation D7).
  styles: `
    :host { --crwd-notice-action-bg: var(--color-accent, #4c17ca); }
  `,
})
export class NotificationsNudgeComponent {
  private readonly desktop = inject(DesktopNotificationService);
  private readonly platform = inject(PlatformService);
  private readonly translate = inject(TranslateService);

  readonly state = signal<'idle' | 'denied'>('idle');
  readonly busy = signal(false);
  readonly visible = signal(this.computeVisible());

  private computeVisible(): boolean {
    return shouldShowNotificationsNudge({
      supported: this.desktop.supported,
      permission: this.desktop.supported ? Notification.permission : null,
      inShell: this.platform.is('shell'),
      dismissedAt: readDismissedAt(),
      now: Date.now(),
    });
  }

  async enable(): Promise<void> {
    this.busy.set(true);
    try {
      // Requests the permission on this click and persists the settings toggle.
      const result = await this.desktop.setEnabled(true);
      if (result.ok) {
        this.sendTestNotification();
        this.visible.set(false);
        return;
      }
      if (result.denied) {
        this.state.set('denied');
      }
      // Prompt dismissed without an answer: the card stays, the button retries.
    } finally {
      this.busy.set(false);
    }
  }

  dismiss(): void {
    writeDismissedAt(Date.now());
    this.visible.set(false);
  }

  private sendTestNotification(): void {
    try {
      const notification = new Notification('Crewdle', {
        body: this.translate.instant('notifications-nudge.test-body'),
        icon: '/assets/icon/favicon.png',
      });
      notification.onclick = () => window.focus();
    } catch { /* best effort */ }
  }
}
